import { Box, Stack } from '@mui/joy';
import { X } from '@phosphor-icons/react';
import { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components/Button';
import { BellNotificationSeverity } from 'notifications/enums';
import {
  getNotificationAlertColors,
  getNotificationSeverityIcon,
} from 'notifications/utils/notification';
import { Color, Direction, Variant } from 'theme/enums';

interface IProps {
  severity: BellNotificationSeverity;
  children: ReactNode;
  onClick?: () => void;
  onClose: () => void;
}

export const NOTIFICATION_ALERT_WIDTH = 476;

const ICON_SIZE = 20;
const CLOSE_ICON_SIZE = 16;

// The toast card of the Alert design: a tinted card per severity around its content, with a Close
// text action.
export const NotificationAlert = ({ severity, children, onClick, onClose }: IProps) => {
  const { t } = useTranslation();

  const { backgroundColor, borderColor } = getNotificationAlertColors(severity);
  const { color: iconColor, icon: Icon } = getNotificationSeverityIcon(severity);

  const handleOnClose = (event: MouseEvent) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Stack
      data-testid={`notificationAlert-${severity}`}
      direction={Direction.ROW}
      onClick={onClick}
      sx={{
        gap: 1.5,
        paddingY: 1.5,
        paddingLeft: 2,
        paddingRight: 1,
        border: '1px solid',
        borderColor,
        borderRadius: 'xl',
        backgroundColor,
        boxShadow: 'md',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* Top-aligned on the title's first 24px line: titles wrap. */}
      <Box
        component="span"
        aria-hidden
        data-testid={`notificationAlertIcon-${severity}`}
        sx={{ display: 'flex', flexShrink: 0, marginTop: '2px', color: iconColor }}
      >
        <Icon size={ICON_SIZE} />
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>{children}</Box>

      <Button
        onClick={handleOnClose}
        text={t('buttonLabelClose')}
        endDecorator={<X size={CLOSE_ICON_SIZE} />}
        variant={Variant.PLAIN}
        color={Color.NEUTRAL}
        sx={{ flexShrink: 0, alignSelf: 'flex-start', color: 'text.primary' }}
      />
    </Stack>
  );
};
